import {
    ref,
    get,
    set,
    onValue,
    off,
    update,
    remove,
    onDisconnect,
} from "firebase/database";
import { signOut } from "firebase/auth";
import { db, auth } from "@/firebaseConfig";
import { v4 as uuidv4 } from "uuid";
import store from "@/store";
import {
    isProjectActive,
    isProjectTeamLead,
} from "@/utils/projectUtils";

/** Admin: write a new timestamp so all clients know to restart. */
export async function sendApplicationRestartSignal() {
    const restartRef = ref(db, "appRestart");
    await set(restartRef, Date.now());
}

/**
 * Clients: subscribe once to /appRestart.
 * Returns a function you can call to stop the subscription.
 */
export function startApplicationRestartListener(newRestartTimestamp) {
    const restartRef = ref(db, "appRestart");
    return onValue(restartRef, (snapshot) => {
        const restartTimestamp = Number(snapshot.val() || 0);
        if (restartTimestamp > 0) {
            newRestartTimestamp(restartTimestamp);
        }
    });
}

/**
 * Listen for all projects in real-time and apply filtering based on user permissions.
 * @param {Function} callback - Function to handle the fetched projects data.
 * @returns {Function} Unsubscribe function to stop listening for project updates.
 */
export function startProjectsListener(callback) {
    const projectsRef = ref(db, "projects");

    onValue(projectsRef, (snapshot) => {
        // Read rights/user each snapshot so role changes apply without stale capture.
        const viewProjects = store.state.rights?.viewProjects || "";
        const userId = store.getters.getUser?.uid;
        const userProjects = store.getters.getUser?.projects || {};
        const userProjectIds = Object.keys(userProjects);

        const projects = snapshot.val();
        const projectsWithID = projects
            ? Object.entries(projects).map(([key, project]) => ({
                  ...project,
                  projectId: key,
              }))
            : [];

        let managedProjects = [];
        let workingProjects = [];

        // Filter "mine" projects (managed by the user)
        if (viewProjects.includes("mine") && userId) {
            managedProjects = projectsWithID.filter((project) =>
                isProjectTeamLead(project, userId)
            );
        }

        // Filter "self" projects (where the user is assigned)
        if (viewProjects.includes("self") && userProjectIds.length) {
            workingProjects = projectsWithID.filter((project) =>
                userProjectIds.includes(project.projectId)
            );
        }

        // Only admins get every project; empty mine/self must not escalate to all.
        if (viewProjects.includes("all")) {
            managedProjects = projectsWithID;
        }

        // Day-to-day lists hide inactive projects; admins keep them to reactivate.
        const canViewInactive = viewProjects.includes("all");
        if (!canViewInactive) {
            managedProjects = managedProjects.filter(isProjectActive);
            workingProjects = workingProjects.filter(isProjectActive);
        }

        // allProjects: full snapshot for name lookups
        callback({ managedProjects, workingProjects, allProjects: projectsWithID });
    });

    // Return the function to detach the listener
    return () => off(projectsRef);
}

/**
 * Whether the signed-in role may subscribe to /users in RTDB.
 * Only admin (viewUsers "all") and team lead (viewUsers "mine") have that permission.
 * @param {string|undefined} viewUsers
 * @returns {boolean}
 */
export function canListenToAllUsers(viewUsers) {
    return viewUsers === "all" || viewUsers === "mine";
}

/**
 * Listen for all users in real-time and apply filtering based on user permissions.
 * Filters out TimeEntries older than 45 days to optimize performance.
 * The calendar day exactly 45 days ago is migrated out of RTDB; the client window starts at
 * local midnight 44 days ago so that edge day is not included.
 * @param {Function} callback - Function to handle the fetched users data.
 * @returns {Function} Unsubscribe function to stop listening for user updates.
 */
export function startUsersListener(callback) {
    const viewUsersAtSubscribe = store.state.rights?.viewUsers;
    if (!canListenToAllUsers(viewUsersAtSubscribe)) {
        callback([]);
        return () => {};
    }

    const userId = store.getters.getUser?.uid;
    const usersRef = ref(db, "users");

    onValue(
        usersRef,
        (snapshot) => {
            if (!auth.currentUser || auth.currentUser.uid !== userId) {
                return;
            }

            // Fresh rights + managed projects each snapshot (no stale capture).
            const viewUsers = store.state.rights?.viewUsers;
            if (!canListenToAllUsers(viewUsers)) {
                callback([]);
                return;
            }

            const users = snapshot.val();
            if (!users) {
                callback([]);
                return;
            }

            const allUsers = Object.entries(users).map(([key, user]) => ({
                ...user,
                uid: key,
            }));

            let filteredUsers = [];

            if (viewUsers === "all") {
                filteredUsers = allUsers;
            } else if (viewUsers === "mine") {
                const teamLeadProjects = store.state.managedProjects || [];
                if (!teamLeadProjects.length) {
                    // Empty managed set → no visible users (do not keep a prior full list).
                    callback([]);
                    return;
                }
                const teamLeadProjectIds = teamLeadProjects.map(
                    (project) => project.projectId
                );
                filteredUsers = allUsers.filter(
                    (user) =>
                        user.projects &&
                        Object.keys(user.projects).some((projectId) =>
                            teamLeadProjectIds.includes(projectId)
                        )
                );
            }

            // Day 45 is migrated to Firestore; keep RTDB data from local midnight 44 days ago.
            const fortyFiveDaysAgo = new Date(
                new Date().setDate(new Date().getDate() - 44)
            ).setHours(0, 0, 0, 0);
            filteredUsers = filteredUsers.map((user) => {
                if (user.timeEntries) {
                    const filteredTimeEntries = Object.fromEntries(
                        Object.entries(user.timeEntries).filter(
                            ([, entry]) =>
                                entry.startTime &&
                                entry.startTime >= fortyFiveDaysAgo
                        )
                    );
                    return {
                        ...user,
                        timeEntries: filteredTimeEntries,
                    };
                }
                return user;
            });

            callback(filteredUsers);
        },
        (error) => {
            console.error("Error fetching users data:", error);
        }
    );

    return () => off(usersRef);
}

/**
 * Listens for changes to a specific user's data in real-time from Firebase.
 * This function manages user data, rights fetching, and conditional project listener activation.
 *
 * @param {string} userId - The unique identifier of the user to listen for.
 * @param {Function} callback - A callback function invoked whenever the user's data is updated.
 *   - The callback receives an object containing the user's data, including their rights and UID.
 *   - If an error occurs or the user is not found, `null` is passed to the callback.
 * @returns {Function} A function to unsubscribe from the listener.
 *
 * ### Functionality:
 * 1. **Real-Time User Data Fetching**:
 *    - Sets up a real-time listener on the user's data in Firebase.
 *    - Updates are triggered whenever the user's data changes in the database.
 *
 * 2. **Rights Fetching**:
 *    - Fetches the user's rights from Firebase based on their role.
 *    - Rights are added to the user's data and passed to the callback.
 *    - Rights are also committed to Vuex for centralized state management.
 *
 * 3. **Project Listener Activation**:
 *    - Tracks the length of the `user.projects` property.
 *    - If the projectIds of `user.projects` changes, the `startProjectsListener` function is dispatched.
 */
export function startUserListener(userId, callback) {
    const userRef = ref(db, `users/${userId}`);

    let previousProjectIdsKey = "";
    let previousRole = null;
    let cachedRights = null;

    onValue(
        userRef,
        async (snapshot) => {
            if (!auth.currentUser || auth.currentUser.uid !== userId) return;

            if (!snapshot.exists()) {
                callback(null);
                return;
            }

            const userData = snapshot.val();
            const currentRole = userData.role || null;

            // Only (re)fetch rights if role changed
            let roleChanged = false;
            if (currentRole !== previousRole) {
                roleChanged = previousRole !== null; // skip restart on first load
                previousRole = currentRole;
                try {
                    cachedRights = currentRole
                        ? await fetchRightsByRole(currentRole)
                        : null;
                    store.commit("setRights", cachedRights);
                } catch (err) {
                    console.error("Error fetching rights:", err);
                    cachedRights = null;
                    store.commit("setRights", null);
                }
            }

            const projectIds = Object.keys(userData.projects || {});
            const currentProjectIdsKey = projectIds.slice().sort().join("|");
            const assignmentsChanged =
                currentProjectIdsKey !== previousProjectIdsKey;

            // Pass user with the latest cached rights
            const updatedUserData = {
                ...userData,
                uid: userId,
                rights: cachedRights,
            };
            callback(updatedUserData);

            // Restart project/user projections when role or assignments change.
            // For viewUsers "mine", users are refreshed from startProjectsListener
            // after managedProjects is committed (avoids filtering with a stale set).
            if (roleChanged || assignmentsChanged) {
                previousProjectIdsKey = currentProjectIdsKey;
                store.dispatch("startProjectsListener", userId);
                const viewUsers = cachedRights?.viewUsers;
                if (viewUsers === "all") {
                    store.dispatch("startUsersListener");
                } else if (!canListenToAllUsers(viewUsers)) {
                    store.commit("setUsers", []);
                }
            }
        },
        (error) => {
            console.error("Error fetching user data:", error);
        }
    );

    return () => {
        off(userRef);
    };
}

/**
 * Log out the currently authenticated user and update their status in the database.
 * @throws Will throw an error if no authenticated user is found or if the logout fails.
 */
export async function logoutUser(user = null) {
    let userId;

    if (user) {
        // If a user parameter is provided, use its UID
        userId = user.uid;
    } else {
        // Fallback to the currently authenticated user
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) {
            throw new Error("No authenticated user found.");
        }
        userId = firebaseUser.uid;
    }

    const userRef = ref(db, `users/${userId}`);

    try {
        // Update the user's status in Firebase
        await update(userRef, {
            userStatus: "loggedOut",
            sessionId: null,
        });

        // If we're logging out the currently authenticated user, sign out
        if (!user) {
            await signOut(auth);
            localStorage.removeItem("sessionId"); // Remove sessionId
        }
    } catch (error) {
        console.error("[logoutUser] Error during logout:", error);
        throw error;
    }
}

/**
 * Handle the sign-in process for a user, including data retrieval and redirection.
 * @param {Object} user - The authenticated user object.
 * @param {Object} router - The Vue Router instance for navigation.
 */
export async function handleSignIn(user, router, setLoading) {
    try {
        const startTime = Date.now();
        const minimumLoadTime = 6000; // 6 seconds minimum

        const sessionId = uuidv4();
        store.commit("setSessionId", sessionId);

        const userRef = ref(db, `users/${user.uid}`);
        await update(userRef, {
            userStatus: "loggedIn",
            sessionId: sessionId,
        });

        // Wait for user data
        let userData = store.getters.getUser;
        let attempts = 0;
        while (!userData && attempts < 40) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            userData = store.getters.getUser;
            attempts++;
        }

        if (!userData || userData.accountStatus !== "active") {
            if (!userData) {
                alert("No user data found. Please sign up first.");
            } else {
                alert("Your account is not active. Please contact admin.");
                router.push("/");
            }
            if (setLoading) setLoading(false);
            return;
        }

        // Start listeners
        store.dispatch("startSessionListener", user.uid);
        const needsUsersList = canListenToAllUsers(store.state.rights?.viewUsers);
        if (needsUsersList) {
            store.dispatch("startUsersListener");
        }

        // Wait for data to load
        const maxWait = 2000;
        const dataLoadStartTime = Date.now();
        while (
            Date.now() - dataLoadStartTime < maxWait &&
            ((needsUsersList && !store.getters.getUsers) ||
                !store.getters.getManagedProjects)
        ) {
            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Ensure minimum 6 seconds total
        const elapsedLoadTime = Date.now() - startTime;
        if (elapsedLoadTime < minimumLoadTime) {
            await new Promise((resolve) => setTimeout(resolve, minimumLoadTime - elapsedLoadTime));
        }

        router.push("/userdashboard");
        if (setLoading) setLoading(false);
    } catch (error) {
        console.error("Error handling sign-in:", error);
        alert("Failed to sign in. Please try again.");
        if (setLoading) setLoading(false); // Stop loading on error
    }
}

/**
 * Update a specific project's data in the database.
 * @param {string} projectId - The ID of the project to update.
 * @param {Object} updatedProject - The updated project data.
 * @throws Will throw an error if the update fails.
 */
export async function updateProjectInDatabase(projectId, updatedProject) {
    try {
        const projectRef = ref(db, `projects/${projectId}`);
        await update(projectRef, updatedProject);
    } catch (error) {
        console.error("Error updating project in database:", error);
        throw error;
    }
}

/**
 * Fetch rights associated with a specific role from Firebase.
 * @param {string} role - The role to fetch rights for.
 * @returns {Object|null} The rights data, or null if no rights are found.
 */
export const fetchRightsByRole = async (role) => {
    try {
        const rightsRef = ref(db, `rights/${role}`);
        const snapshot = await get(rightsRef);

        if (snapshot.exists()) {
            return snapshot.val();
        } else {
            console.error(`No rights found for role: ${role}`);
            return null;
        }
    } catch (error) {
        console.error("Error fetching rights:", error);
        return null;
    }
};

export const fetchAllProjects = async () => {
    try {
        const projectsRef = ref(db, "projects");
        const snapshot = await get(projectsRef);

        if (snapshot.exists()) {
            const projects = snapshot.val();
            // Map projects into an array with projectId included
            return Object.entries(projects).map(([key, project]) => ({
                ...project,
                projectId: key,
            }));
        } else {
            console.error("No projects found in the database.");
            return [];
        }
    } catch (error) {
        console.error("Error fetching projects:", error);
        return [];
    }
};
/**
 * Performs batch updates on multiple projects in the database.
 * @param {Object} updates - An object containing the updates to be made.
 * @throws Will throw an error if the update fails.
 */
export async function updateMultipleProjectsInDatabase(updates) {
    try {
        // Assuming you're using Firebase Realtime Database
        await update(ref(db), updates);
    } catch (error) {
        console.error("Error updating multiple projects in database:", error);
        throw error;
    }
}
